import React, { useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';

/**
 * Custom Gradient Heatmap Library
 * 
 * Transforms variable option counts (3, 5, or 10) into a standardized 10-segment gradient visualization.
 * 
 * Transformation rules:
 * - 10 options: Display as-is (10 segments of 10% each)
 * - 5 options: Duplicate each option (10 segments of 10% each, but same color in pairs)
 * - 3 options: First and last options get 40% each (4 segments), middle gets 20% (2 segments)
 */

/**
 * Transform response data based on option count
 * @param {Array} responseCounts - Array of response counts for a question
 * @returns {Array} Array of 10 segments with {count, width, color, originalIndex}
 */
const transformToTenSegments = (responseCounts) => {
    const optionCount = responseCounts.length;
    const segments = [];

    if (optionCount === 10) {
        // Already 10 options, use as-is
        responseCounts.forEach((count, index) => {
            segments.push({
                count,
                width: 10, // 10% width
                originalIndex: index,
                segmentCount: 1
            });
        });
    } else if (optionCount === 5) {
        // 5 options: duplicate each to get 10 segments
        responseCounts.forEach((count, index) => {
            // Add two segments for each option
            segments.push({
                count,
                width: 10, // 10% width
                originalIndex: index,
                segmentCount: 2,
                segmentPosition: 0
            });
            segments.push({
                count,
                width: 10, // 10% width
                originalIndex: index,
                segmentCount: 2,
                segmentPosition: 1
            });
        });
    } else if (optionCount === 3) {
        // 3 options: quadruple first and last (40% each), double middle (20%)
        const [first, middle, last] = responseCounts;

        // First option: 4 segments (40%)
        for (let i = 0; i < 4; i++) {
            segments.push({
                count: first,
                width: 10, // 10% width
                originalIndex: 0,
                segmentCount: 4,
                segmentPosition: i
            });
        }

        // Middle option: 2 segments (20%)
        for (let i = 0; i < 2; i++) {
            segments.push({
                count: middle,
                width: 10, // 10% width
                originalIndex: 1,
                segmentCount: 2,
                segmentPosition: i
            });
        }

        // Last option: 4 segments (40%)
        for (let i = 0; i < 4; i++) {
            segments.push({
                count: last,
                width: 10, // 10% width
                originalIndex: 2,
                segmentCount: 4,
                segmentPosition: i
            });
        }
    } else {
        // Fallback for other counts - just repeat evenly
        const segmentsPerOption = Math.floor(10 / optionCount);
        const remainder = 10 % optionCount;

        let currentSegment = 0;
        responseCounts.forEach((count, index) => {
            const segCount = segmentsPerOption + (index < remainder ? 1 : 0);
            for (let i = 0; i < segCount; i++) {
                segments.push({
                    count,
                    width: 10,
                    originalIndex: index,
                    segmentCount: segCount,
                    segmentPosition: i
                });
                currentSegment++;
            }
        });
    }

    return segments;
};

/**
 * Calculate color intensity based on value
 * @param {number} value - The count value
 * @param {number} maxValue - Maximum value across all data
 * @returns {string} RGBA color string
 */
const getColorIntensity = (value, maxValue) => {
    const intensity = maxValue > 0 ? value / maxValue : 0;
    return `rgba(25, 118, 210, ${intensity})`;
};

/**
 * Group consecutive segments that belong to the same option
 * @param {Array} segments - Array of segment objects
 * @returns {Array} Array of segment groups
 */
const groupSegments = (segments) => {
    const groups = [];
    let currentGroup = null;

    segments.forEach((segment, index) => {
        if (segment.segmentPosition === 0 || segment.segmentCount === 1) {
            // Start a new group
            if (currentGroup) {
                groups.push(currentGroup);
            }
            currentGroup = {
                count: segment.count,
                width: segment.width * segment.segmentCount,
                segmentCount: segment.segmentCount,
                originalIndex: segment.originalIndex,
                segments: [segment]
            };
        } else {
            // Add to current group
            currentGroup.segments.push(segment);
        }
    });

    // Push the last group
    if (currentGroup) {
        groups.push(currentGroup);
    }

    return groups;
};

/**
 * GradientHeatmapRow Component
 * Renders a single row of the heatmap with gradient segments
 */
const GradientHeatmapRow = ({ question, responseCounts, maxValue, questionTextWidth = 30, choices = [] }) => {
    const segments = transformToTenSegments(responseCounts);
    const groups = groupSegments(segments);
    const gradientBarWidth = 100 - questionTextWidth;

    return (
        <Box
            sx={{
                display: 'flex',
                width: '100%',
                mb: 0.5,
                alignItems: 'stretch',
                minHeight: 40
            }}
        >
            {/* Question Text - Fixed Width */}
            <Tooltip title={question.text} placement="bottom">
                <Box
                    sx={{
                        width: `${questionTextWidth}%`,
                        p: 1.5,
                        fontSize: '0.85rem',
                        borderRight: '2px solid #e0e0e0',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: '#f9f9f9',
                        fontWeight: 500
                    }}
                >
                    {question.text}
                </Box>
            </Tooltip>

            {/* Gradient Bar - Remaining Width */}
            <Box
                sx={{
                    width: `${gradientBarWidth}%`,
                    display: 'flex',
                    position: 'relative'
                }}
            >
                {groups.map((group, index) => {
                    const backgroundColor = getColorIntensity(group.count, maxValue);
                    const choiceText = choices[group.originalIndex] || `Option ${group.originalIndex + 1}`;

                    return (
                        <Tooltip
                            key={index}
                            title={
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {choiceText}
                                    </Typography>
                                    <Typography variant="body2">
                                        {group.count} responses
                                    </Typography>
                                </Box>
                            }
                            placement="top"
                        >
                            <Box
                                sx={{
                                    width: `${group.width}%`,
                                    backgroundColor,
                                    border: '1px solid rgba(255, 255, 255, 0.5)',
                                    borderLeft: '2px solid #fff',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    color: group.count > maxValue * 0.5 ? 'white' : 'rgba(0, 0, 0, 0.7)',
                                    '&:hover': {
                                        opacity: 0.85,
                                        transform: 'scaleY(1.05)',
                                        zIndex: 1,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                    }
                                }}
                            >
                                {group.count}
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>
        </Box>
    );
};

/**
 * GradientHeatmap Component
 * Main component for rendering the gradient-based heatmap
 * 
 * @param {Object} props
 * @param {Array} props.questions - Array of question objects with {text, type} properties
 * @param {Array} props.responseCounts - 2D array of response counts [question][option]
 * @param {Array} props.responseTypes - Array of response type objects with {id, type, choices}
 * @param {number} props.questionTextWidth - Percentage width for question text (default: 30)
 */
const GradientHeatmap = ({ questions, responseCounts, responseTypes = [], questionTextWidth = 30 }) => {
    // Calculate max value across all data for color intensity
    const maxValue = Math.max(...responseCounts.flat());

    // Helper function to get choices for a question
    const getChoicesForQuestion = (questionIndex) => {
        const questionType = questions[questionIndex].type;
        const responseType = responseTypes.find(rt => rt.id === questionType);
        return responseType ? responseType.choices : [];
    };

    return (
        <Box sx={{ width: '100%', overflow: 'auto' }}>
            <Box sx={{ minWidth: 800 }}>
                {/* Header */}

                {/* Rows */}
                {questions.map((question, index) => (
                    <GradientHeatmapRow
                        key={index}
                        question={question}
                        responseCounts={responseCounts[index]}
                        maxValue={maxValue}
                        questionTextWidth={questionTextWidth}
                        choices={getChoicesForQuestion(index)}
                    />
                ))}

                {/* Legend */}
            </Box>
        </Box>
    );
};

export default GradientHeatmap;

